import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Permission, Role } from '@shared/roles';
import type { Me, Membership } from '@shared/apiTypes';
import { api, getActiveOrg, setActiveOrg } from './api';

export type { Me, Membership } from '@shared/apiTypes';

interface SessionValue {
  me: Me | null;
  loading: boolean;
  active: Membership | null;
  choose: (orgId: string) => void;
  can: (...p: Permission[]) => boolean;
  has: (...r: Role[]) => boolean;
  refresh: () => Promise<unknown>;
  logout: () => Promise<void>;
}

const Ctx = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [orgId, setOrgId] = useState<string | null>(getActiveOrg());
  const q = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<Me>('/api/me', { orgId: null });
      } catch (e) {
        if ((e as { status?: number }).status === 401) return null;
        throw e;
      }
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    const onUnauth = () => qc.setQueryData(['me'], null);
    window.addEventListener('si:unauthenticated', onUnauth);
    return () => window.removeEventListener('si:unauthenticated', onUnauth);
  }, [qc]);

  const me = q.data ?? null;
  const active = useMemo(() => {
    if (!me) return null;
    const ms = me.memberships;
    if (me.station) return ms.find((m) => m.orgId === me.station!.orgId) ?? null;
    return ms.find((m) => m.orgId === orgId) ?? (ms.length === 1 ? ms[0] : null);
  }, [me, orgId]);

  useEffect(() => {
    if (active && active.orgId !== getActiveOrg()) setActiveOrg(active.orgId);
  }, [active]);

  const choose = useCallback(
    (id: string) => {
      setActiveOrg(id);
      setOrgId(id);
      qc.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' });
    },
    [qc],
  );

  const value: SessionValue = {
    me,
    loading: q.isLoading,
    active,
    choose,
    can: (...p) => Boolean(active && p.some((x) => active.permissions.includes(x))),
    has: (...r) => Boolean(active && r.some((x) => active.roles.includes(x))),
    refresh: async () => {
      // A PIN switch or counter lock must not leave the previous person's
      // cached business data visible on the shared device.
      qc.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' });
      await qc.invalidateQueries({ queryKey: ['me'] });
    },
    logout: async () => {
      await api.post('/api/auth/logout').catch(() => undefined);
      setActiveOrg(null);
      qc.clear();
      window.location.href = '/login';
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('SessionProvider missing');
  return v;
}

/** Where each role starts. */
export function homeFor(m: Membership | null): string {
  if (!m) return '/choose';
  if (m.roles.includes('PLATFORM_OPERATOR')) return '/platform';
  if (m.roles.includes('SUPPLIER_ADMIN') || m.roles.includes('SUPPLIER_ACCOUNTS')) return '/supplier';
  if (m.roles.includes('SUPPLIER_ATTENDANT')) return '/station';
  if (m.roles.some((r) => r === 'BUYER_ADMIN' || r === 'BUYER_APPROVER' || r === 'BUYER_ACCOUNTS')) return '/buyer';
  if (m.roles.includes('DRIVER')) return '/driver';
  return '/choose';
}
